import type { BadgeColor } from 'app/components/ui/badge'
import type { FailureGroup, SmsAgentOpsCounts } from 'app/types/sms-agent-ops'

export const FAILURE_GROUPS: FailureGroup[] = [
  `undelivered`,
  `carrierFailed`,
  `noConsent`,
  `quietHours`,
  `stuckPending`,
]

export const FAILURE_GROUP_LABEL: Record<FailureGroup, string> = {
  undelivered: `Undelivered`,
  carrierFailed: `Carrier Failed`,
  noConsent: `No Consent`,
  quietHours: `Quiet Hours`,
  stuckPending: `Stuck Pending`,
}

export const FAILURE_GROUP_COLOR: Record<FailureGroup, BadgeColor> = {
  undelivered: `orange`,
  carrierFailed: `red`,
  noConsent: `purple`,
  quietHours: `amber`,
  stuckPending: `gray`,
}

export const FAILURE_GROUP_COUNT_KEY: Record<FailureGroup, keyof SmsAgentOpsCounts> = {
  undelivered: `undeliveredTexts`,
  carrierFailed: `carrierFailedTexts`,
  noConsent: `noConsentTexts`,
  quietHours: `quietHoursTexts`,
  stuckPending: `stuckPendingTexts`,
}

/**
 * A failed text's group, or undefined when it did not fail (plan D12). Policy blocks
 * are written as `failed` with our own error code, so the code wins over the status.
 * Providers disagree on vocabulary: Twilio says `undelivered`, AWS and Twilio `failed`.
 */
export function failureGroupFor(message: {
  status?: string | null
  errorCode?: string | null
}): FailureGroup | undefined {
  if (message.errorCode === `NO_MARKETING_CONSENT`) return `noConsent`
  if (message.errorCode === `QUIET_HOURS`) return `quietHours`
  if (message.status === `undelivered`) return `undelivered`
  if (message.status === `failed`) return `carrierFailed`
  if (message.status === `pending`) return `stuckPending`
  return undefined
}

/** Error codes worth naming. Twilio's numeric codes, plus the policy codes our API writes. */
const ERROR_CODE_LABEL: Record<string, string> = {
  NO_MARKETING_CONSENT: `No marketing consent`,
  QUIET_HOURS: `Quiet hours`,
  OPTED_OUT: `Opted out`,
  '21211': `Invalid "To" number`,
  '21610': `Recipient unsubscribed`,
  '21614': `Not a mobile number`,
  '30003': `Unreachable handset`,
  '30004': `Message blocked`,
  '30005': `Unknown destination`,
  '30006': `Landline or unreachable carrier`,
  '30007': `Carrier filtered (spam)`,
  '30008': `Unknown error`,
  '30032': `Toll-free number not verified`,
  '30034': `Unregistered 10DLC number`,
}

/** Label for an error code. An unknown code is shown as-is so nothing hides. */
export function errorCodeLabel(code?: string | null): string {
  if (!code) return `No error code`
  return ERROR_CODE_LABEL[code] ?? `Error ${code}`
}
